<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import type { CustomerUser } from '@/types/generated'
  import { OrganisationsService, RaMicroExportIntegrationTypeEnum } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import * as yup from 'yup'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed<CustomerUser>(() => useUserStore().user)

  // RA MICRO integration fields
  const isRaMicroEnabled = ref(false)
  const raMicroExportType = ref<string | null>(null)
  const raMicroOmaEmailId = ref('')
  const raMicroOmaEmail = ref('')

  // Options for the export type select field
  const exportTypeOptions = [
    {
      value: RaMicroExportIntegrationTypeEnum.DOWNLOAD,
      title: 'Dateidownload'
    },
    {
      value: RaMicroExportIntegrationTypeEnum.OMA_EMAIL,
      title: 'RA-MICRO-Postfach über die OMA-Schnittstelle'
    }
  ]

  const { t } = useTranslation({
    en: {
      description: 'Configure RA-MICRO integration settings for JUPUS.',
      enableRaMicro: 'Enable RA-MICRO Integration',
      exportType: 'Export Type',
      selectExportType: 'Select export type...',
      omaEmailId: 'RAMOMI Code',
      omaEmailIdPlaceholder: 'Enter OMA Email ID',
      omaEmail: 'OMA Email Address',
      omaEmailPlaceholder: 'Enter OMA Email address (e.g. oma@@yourfirm.com)',
      invalidUuid: 'Please enter a valid UUID format (e.g., 72155790-00df-49e5-8cd6-7fa142430abc)',
      invalidEmail: 'Please enter a valid email address',
      saving: 'Saving...',
      setupInfo: 'The RA-MICRO interface can be set up in two ways:',
      fileDownload: 'As file download:',
      moreInfoText: 'More information can be found',
      here: 'here',
      omaInterface: 'As case creation from RA-MICRO mailbox via OMA interface:',
      detailsText: 'Further details can be found'
    },
    de: {
      description: 'Die RA-MICRO Schnittstelle ',
      enableRaMicro: 'RA-MICRO Integration aktivieren',
      exportType: 'Export-Typ',
      selectExportType: 'Export-Typ auswählen...',
      omaEmailId: 'RAMOMI Code',
      omaEmailIdPlaceholder: 'Z.B. 72155790-00df-49e5-8cd6-7fa142430abc',
      omaEmail: 'OMA E-Mail Adresse',
      omaEmailPlaceholder: 'Z.B. oma@@ihrekanzlei.de',
      invalidUuid:
        'Bitte geben Sie ein gültiges UUID-Format ein (z.B. 72155790-00df-49e5-8cd6-7fa142430abc)',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      saving: 'Speichern...',
      setupInfo: 'Die RA-MICRO Schnittstelle kann auf zwei Arten eingerichtet werden:',
      fileDownload: 'Als Dateidownload:',
      moreInfoText: 'Weitere Informationen finden Sie',
      here: 'hier',
      omaInterface: 'Als Aktenanlage aus dem RA-MICRO-Postfach über die OMA-Schnittstelle:',
      detailsText: 'Nähere Details finden Sie'
    }
  })

  const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

  // Validation schema
  const validationSchema = {
    ra_micro_oma_email_id: yup
      .string()
      .transform((value) => (value === '' ? null : value)) // Transform empty string to null
      .matches(uuidv4Regex, t('invalidUuid'))
      .nullable()
      .optional(), // Make field optional
    ra_micro_oma_email: yup.string().email(t('invalidEmail')).nullable()
  }

  interface FormData {
    ra_micro_oma_email_id: string
    ra_micro_oma_email: string
  }

  const { formData, formErrors, touchedFields, validateField, handleFieldChange } =
    useFormWithValidation<FormData>(
      {
        ra_micro_oma_email_id: '',
        ra_micro_oma_email: ''
      },
      validationSchema
    )

  const updateRaMicroSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: Record<string, any> = {
        is_ra_micro_export_integration_enabled: isRaMicroEnabled.value
      }

      // Only include export type if it has a value
      if (raMicroExportType.value) {
        data.ra_micro_export_integration_type = raMicroExportType.value
      }

      // Only include OMA email ID if it's required and has a value
      if (
        raMicroExportType.value === RaMicroExportIntegrationTypeEnum.OMA_EMAIL &&
        raMicroOmaEmailId.value
      ) {
        // Validate UUID format
        const isValid = await validateField('ra_micro_oma_email_id', raMicroOmaEmailId.value)
        if (isValid) {
          data.ra_micro_oma_email_id = raMicroOmaEmailId.value
        }
      }

      // Only include OMA email if it's required and has a value
      if (
        raMicroExportType.value === RaMicroExportIntegrationTypeEnum.OMA_EMAIL &&
        raMicroOmaEmail.value
      ) {
        // Validate email format
        const isValid = await validateField('ra_micro_oma_email', raMicroOmaEmail.value)
        if (isValid) {
          data.ra_micro_oma_email = raMicroOmaEmail.value
        }
      }

      await updateOrganisation(data)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  const updateOrganisation = async (data: any) => {
    try {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: user.value!.org.uuid,
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    // Initialize form with existing values if available
    if (user.value?.org) {
      isRaMicroEnabled.value = user.value.org.is_ra_micro_export_integration_enabled || false
      raMicroExportType.value = user.value.org.ra_micro_export_integration_type || null
      raMicroOmaEmailId.value = user.value.org.ra_micro_oma_email_id || ''
      formData.ra_micro_oma_email_id = raMicroOmaEmailId.value
      raMicroOmaEmail.value = user.value.org.ra_micro_oma_email || ''
      formData.ra_micro_oma_email = raMicroOmaEmail.value
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isRaMicroEnabled, raMicroExportType], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateRaMicroSettings()
    }
  })

  // Add watchers for the input fields
  watch(raMicroOmaEmailId, (newValue) => {
    formData.ra_micro_oma_email_id = newValue
    handleFieldChange('ra_micro_oma_email_id', newValue)
    if (user.value) {
      updateRaMicroSettings()
    }
  })

  watch(raMicroOmaEmail, (newValue) => {
    formData.ra_micro_oma_email = newValue
    handleFieldChange('ra_micro_oma_email', newValue)
    if (user.value) {
      updateRaMicroSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div class="mb-6">
        <!-- RA-MICRO Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable RA-MICRO Integration -->
          <CheckboxField
            v-model="isRaMicroEnabled"
            name="is_ra_micro_export_integration_enabled"
            :label="t('enableRaMicro')"
          />

          <!-- Export Type Selection (only shown if RA-MICRO is enabled) -->
          <div v-if="isRaMicroEnabled">
            <SelectField
              v-model="raMicroExportType"
              name="ra_micro_export_integration_type"
              :label="t('exportType')"
              :options="exportTypeOptions"
              :select-option-text="t('selectExportType')"
            />

            <!-- Help text moved under controls and styled as subtle text -->
            <div class="mt-2 text-sm text-gray-500">
              <p class="mb-2">
                {{ t('setupInfo') }}
              </p>
              <ul class="list-disc space-y-2 pl-4">
                <li>
                  <span class="font-medium">{{ t('fileDownload') }}</span>
                  <span class="ms-1">{{ t('moreInfoText') }}</span>
                  <a
                    href="https://hilfe.jupus.de/ra-micro-anbindung-an-jupus"
                    target="_blank"
                    class="ms-1 text-purple-600 hover:underline"
                  >
                    {{ t('here') }}
                  </a>
                  .
                </li>
                <li>
                  <span class="font-medium">
                    {{ t('omaInterface') }}
                  </span>
                  <span class="ms-1">{{ t('detailsText') }}</span>
                  <a
                    href="https://hilfe.jupus.de/aktenanlage-in-ra-micro-%C3%BCber-die-oma-schnittstelle"
                    target="_blank"
                    class="ms-1 text-purple-600 hover:underline"
                  >
                    {{ t('here') }}
                  </a>
                  .
                </li>
              </ul>
            </div>
          </div>

          <!-- OMA Email ID (only shown if RA-MICRO is enabled and export type is OMA_EMAIL) -->
          <div
            v-if="
              isRaMicroEnabled && raMicroExportType === RaMicroExportIntegrationTypeEnum.OMA_EMAIL
            "
          >
            <InputField
              v-model="raMicroOmaEmailId"
              name="ra_micro_oma_email_id"
              :label="t('omaEmailId')"
              :placeholder="t('omaEmailIdPlaceholder')"
              :error="
                touchedFields['ra_micro_oma_email_id'] ? formErrors['ra_micro_oma_email_id'] : ''
              "
            />

            <InputField
              v-model="raMicroOmaEmail"
              name="ra_micro_oma_email"
              :label="t('omaEmail')"
              placeholder="Z.B. oma@ihrekanzlei.de"
              type="email"
              :error="touchedFields['ra_micro_oma_email'] ? formErrors['ra_micro_oma_email'] : ''"
            />
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
